import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  User,
  Lock,
  Bell,
  Key,
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Loader2,
  Mail,
  FileText,
  Zap,
} from 'lucide-react'

import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface NotificationSetting {
  id: string
  label: string
  description: string
  enabled: boolean
  icon: typeof Mail
}

export function SettingsPage() {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)

  // Profile state
  const [profileName, setProfileName] = useState(user?.name || '')
  const [profileSaved, setProfileSaved] = useState(false)

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Notification settings
  const [notifications, setNotifications] = useState<NotificationSetting[]>([
    {
      id: 'email_generation_complete',
      label: 'settings.notifications.generationComplete',
      description: 'settings.notifications.generationCompleteDesc',
      enabled: true,
      icon: FileText,
    },
    {
      id: 'email_generation_failed',
      label: 'settings.notifications.generationFailed',
      description: 'settings.notifications.generationFailedDesc',
      enabled: true,
      icon: AlertTriangle,
    },
    {
      id: 'email_schedule_run',
      label: 'settings.notifications.scheduledRun',
      description: 'settings.notifications.scheduledRunDesc',
      enabled: false,
      icon: Zap,
    },
    {
      id: 'email_weekly_digest',
      label: 'settings.notifications.weeklyDigest',
      description: 'settings.notifications.weeklyDigestDesc',
      enabled: true,
      icon: Mail,
    },
  ])

  // API Keys state
  const [openaiKey, setOpenaiKey] = useState('')
  const [anthropicKey, setAnthropicKey] = useState('')
  const [showOpenaiKey, setShowOpenaiKey] = useState(false)
  const [showAnthropicKey, setShowAnthropicKey] = useState(false)
  const [apiKeysSaved, setApiKeysSaved] = useState(false)

  // Personal API Token
  const [apiToken, setApiToken] = useState('geo_live_xxxxxxxxxxxxxxxxxxxxxxxxx')
  const [copiedToken, setCopiedToken] = useState(false)

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setPasswordSuccess(true)
      setPasswordError('')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordSuccess(false), 3000)
    },
    onError: (error: Error) => {
      setPasswordError(error.message || 'Failed to change password')
      setPasswordSuccess(false)
    },
  })

  const handleProfileSave = () => {
    // In a real app, this would call an API
    setProfileSaved(true)
    setTimeout(() => setProfileSaved(false), 3000)
  }

  const handlePasswordChange = () => {
    setPasswordError('')

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    changePasswordMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    })
  }

  const toggleNotification = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, enabled: !n.enabled } : n))
    )
  }

  const handleApiKeysSave = () => {
    // In a real app, this would call an API
    setApiKeysSaved(true)
    setTimeout(() => setApiKeysSaved(false), 3000)
  }

  const handleCopyToken = () => {
    navigator.clipboard.writeText(apiToken)
    setCopiedToken(true)
    setTimeout(() => setCopiedToken(false), 2000)
  }

  const handleRegenerateToken = () => {
    // In a real app, this would call an API
    const newToken = `geo_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`
    setApiToken(newToken)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.description')}</p>
      </div>

      <div className="grid gap-6">
        {/* Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('settings.profile.title')}
            </CardTitle>
            <CardDescription>{t('settings.profile.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('settings.profile.name')}</Label>
                <Input
                  id="name"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('settings.profile.email')}</Label>
                <Input id="email" type="email" defaultValue={user?.email || ''} disabled />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleProfileSave}>
                {profileSaved ? (
                  <>
                    <Check className="me-2 h-4 w-4" />
                    {t('app.saved')}
                  </>
                ) : (
                  t('settings.profile.saveChanges')
                )}
              </Button>
              {profileSaved && (
                <span className="text-sm text-green-600">{t('settings.profile.updateSuccess')}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t('settings.password.title')}
            </CardTitle>
            <CardDescription>{t('settings.password.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="current-password">{t('settings.password.currentPassword')}</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t('settings.password.newPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('settings.password.confirmPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
              {passwordSuccess && (
                <p className="text-sm text-green-600">{t('settings.password.updateSuccess')}</p>
              )}
              <Button
                type="submit"
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t('app.updating')}
                  </>
                ) : (
                  t('settings.password.updatePassword')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('settings.notifications.title')}
            </CardTitle>
            <CardDescription>{t('settings.notifications.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {notifications.map((notification) => {
              const Icon = notification.icon
              return (
                <div
                  key={notification.id}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{t(notification.label)}</p>
                      <p className="text-sm text-muted-foreground">
                        {t(notification.description)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleNotification(notification.id)}
                    className={cn(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                      notification.enabled ? 'bg-primary' : 'bg-muted'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                        notification.enabled ? 'translate-x-6' : 'translate-x-1'
                      )}
                    />
                  </button>
                </div>
              )
            })}
          </CardContent>
        </Card>

        {/* API Keys Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {t('settings.apiKeys.title')}
            </CardTitle>
            <CardDescription>{t('settings.apiKeys.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="openai-key">{t('settings.apiKeys.openaiKey')}</Label>
              <div className="relative">
                <Input
                  id="openai-key"
                  type={showOpenaiKey ? 'text' : 'password'}
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  placeholder="sk-..."
                />
                <button
                  type="button"
                  onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showOpenaiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="anthropic-key">{t('settings.apiKeys.anthropicKey')}</Label>
              <div className="relative">
                <Input
                  id="anthropic-key"
                  type={showAnthropicKey ? 'text' : 'password'}
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  placeholder="sk-ant-..."
                />
                <button
                  type="button"
                  onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showAnthropicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleApiKeysSave}>
                {apiKeysSaved ? (
                  <>
                    <Check className="me-2 h-4 w-4" />
                    {t('app.saved')}
                  </>
                ) : (
                  t('settings.apiKeys.saveKeys')
                )}
              </Button>
              {apiKeysSaved && (
                <span className="text-sm text-green-600">{t('settings.apiKeys.saveSuccess')}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Personal API Token */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {t('settings.personalToken.title')}
            </CardTitle>
            <CardDescription>
              {t('settings.personalToken.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                value={apiToken}
                readOnly
                className="font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={handleCopyToken}>
                {copiedToken ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={handleRegenerateToken}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('settings.personalToken.warning')}
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t('settings.dangerZone.title')}
            </CardTitle>
            <CardDescription>{t('settings.dangerZone.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/5">
              <div>
                <p className="font-medium">{t('settings.dangerZone.deleteAccount')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('settings.dangerZone.deleteAccountDesc')}
                </p>
              </div>
              <Button variant="destructive" size="sm">
                {t('settings.dangerZone.deleteAccount')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
